<script>
    import { onMount } from 'svelte';
    import { createEventDispatcher } from 'svelte';
    import { AssetField } from '../methods/AssetField.js';
    import { ImageField } from '../methods/ImageField.js';
    import { getPublicUrl } from '../methods/getPublicUrl.js';
    import { isPlainObject } from '../methods/isPlainObject.js';
    import { isEmpty } from '../methods/isEmpty.js';

    let { mtappVars, id, storage = $bindable(), storageKey, handle, fieldBlock } = $props();
    let assetFieldInstance;
    if (!isPlainObject(storage[storageKey])) {
        storage[storageKey] = {
            id: '',
            url: '',
            thumbnail: '',
            alt: ''
        };
    }

    let assetAddButtonLabel = $derived(fieldBlock.buttonLabel ? fieldBlock.buttonLabel : `${fieldBlock.label}を選択`);

    const dispatch = createEventDispatcher();
    function triggerInputChange() {
        dispatch('inputchange');
    }

    const assetFieldOptions = {
        thumbnailWidth: fieldBlock.thumbnailWidth || 120,
        thumbnailHeight: fieldBlock.thumbnailHeight || 120,
        uploadDirectory: fieldBlock.uploadDirectory || ''
    };

    onMount(() => {
        // MT9のスクリプトが読み込まれるまで待機する関数
        const initializeAssetField = () => {
            // 画像の場合はImageField、それ以外はAssetFieldを使用
            if (fieldBlock.type === 'image' && window.MT?.AssetUploader) {
                // MT9の場合はImageFieldを使用
                assetFieldInstance = new ImageField(id, handle, assetFieldOptions, storage, storageKey, triggerInputChange);
            } else {
                // MT8以前またはファイルの場合はAssetFieldを使用
                assetFieldInstance = new AssetField(id, fieldBlock.type, handle, assetFieldOptions);
                if (typeof fieldBlock.uploadDirectory === 'string') {
                    window.mtappVars.__system.assetField.uploadDirectories[handle] = fieldBlock.uploadDirectory;
                } else if (Array.isArray(fieldBlock.uploadDirectory)) {
                    window.mtappVars.__system.assetField.uploadDirectories[handle] = [...fieldBlock.uploadDirectory];
                }
            }
        };

        // MT9のスクリプトが読み込まれているかチェック
        if (fieldBlock.type === 'image') {
            if (window.MT?.AssetUploader) {
                initializeAssetField();
            } else {
                // MT9のスクリプトが読み込まれるまで待機
                const checkInterval = setInterval(() => {
                    if (window.MT?.AssetUploader) {
                        clearInterval(checkInterval);
                        initializeAssetField();
                    }
                }, 100); // 100msごとにチェック

                // 3秒後にタイムアウト
                setTimeout(() => {
                    clearInterval(checkInterval);
                    if (!assetFieldInstance) {
                        initializeAssetField();
                    }
                }, 3000);
            }
        } else {
            // ファイルタイプの場合は即座に初期化
            initializeAssetField();
        }
    });

    /**
     * URL からファイル名を取得する
     *
     * @param {string} url
     */
    function getFilename(url) {
        return url ? new URL(url, url.startsWith('http') ? undefined : window.location.origin).pathname.split('/').pop() : '';
    }

    /**
     * ファイルを選択する
     * @param event
     */
    function select(event) {
        event.preventDefault();
        return assetFieldInstance.select();
    }

    /**
     * ファイルを削除する
     * @param event
     */
    function remove(event) {
        event.preventDefault();
        storage[storageKey].id = '';
        storage[storageKey].url = '';
        if (fieldBlock.type === 'image') {
            storage[storageKey].thumbnail = '';
        }
        triggerInputChange();
        return assetFieldInstance.remove();
    }
</script>

<div data-handle={handle}>
    <input
        id={id}
        class="form-control mtapp-mf-field-{fieldBlock.type}"
        type="hidden"
        bind:value={storage[storageKey].id}
        onblur={triggerInputChange} />
    {#if fieldBlock.type === 'image'}
        <input
            id={id + '-thumb'}
            class="form-control mtapp-mf-field-thumbnail"
            type="hidden"
            bind:value={storage[storageKey].thumbnail}
            onblur={triggerInputChange} />
    {/if}
    <input id={id + '-url'} class="form-control" type="hidden" bind:value={storage[storageKey].url} onblur={triggerInputChange} />

    {#if storage[storageKey].url}
        <a href={getPublicUrl(storage[storageKey].url)} target="_blank" class="d-block pb-2">
            {#if fieldBlock.type === 'image' && storage[storageKey].thumbnail}
                <img src={getPublicUrl(storage[storageKey].thumbnail)} class="img-fluid" alt={fieldBlock.label} />
            {:else}
                <svg role="img" class="mt-icon">
                    <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_file'} />
                </svg>
            {/if}
            <span class:d-none={fieldBlock.type === 'image' && fieldBlock.hideFilename && storage[storageKey].thumbnail}
                >{getFilename(storage[storageKey].url)}</span>
        </a>
    {/if}

    <div class="btn-group" role="group">
        <button type="button" class="btn btn-default" onclick={(event) => select(event)}>{assetAddButtonLabel}</button>
        {#if storage[storageKey].url}
            <button
                type="button"
                onclick={(event) => remove(event)}
                class="btn btn-default p-1 assetField-remove-image"
                aria-label="Remove">
                <svg role="img" class="mt-icon">
                    <use xlink:href={mtappVars.static_path + '/images/sprite.svg#ic_trash'} />
                </svg>
            </button>
        {/if}
    </div>

    {#if storage[storageKey].required && isEmpty(storage[storageKey])}
        <span class="text-danger fw-bold">{mtappVars.translate['This field is required']}</span>
    {/if}

    {#if typeof fieldBlock.textFieldLabel !== 'undefined' && fieldBlock.textFieldLabel}
        <div class="mt-3 mb-0">
            <label for={id + '-alt'}>{fieldBlock.textFieldLabel}</label>
            <input id={id + '-alt'} class="form-control" type="text" bind:value={storage[storageKey].alt} onblur={triggerInputChange} />
        </div>
    {/if}
</div>
